import { Box } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { DonutChart } from '../charts/DonutChart';
import { dayTheme, nightTheme, totalTheme } from '../charts/themes';
import { useWindowDimensions } from '@Iot-Bee/standard-web-library';
import { DashboardDeviceData } from '../../interfaces';
import { TimeRange } from '../../types';
import { timestampToMinutes } from '../../util';

interface TotalOverviewProps {
  data: DashboardDeviceData[];
  timeRange: TimeRange;
  isLoading: boolean;
}

interface ChartData {
  insideWorkingHours: { [tag: string]: number };
  outsideWorkingHours: { [tag: string]: number };
}

const TotalOverview: FC<TotalOverviewProps> = ({ data, timeRange, isLoading }) => {
  const { screenwidth } = useWindowDimensions();

  const chartData = useMemo(() => {
    const datas = { insideWorkingHours: {}, outsideWorkingHours: {} } as ChartData;

    data.forEach((device) => {
      device.data.forEach((dataPoint) => {
        const minutes = timestampToMinutes(dataPoint.timestamp);
        const tag = device.tags[0];
        if (minutes >= timeRange[0] && minutes <= timeRange[1]) {
          if (datas.insideWorkingHours[tag] === undefined) {
            datas.insideWorkingHours[tag] = 0;
          }
          datas.insideWorkingHours[tag] += dataPoint.value;
        } else {
          if (datas.outsideWorkingHours[tag] === undefined) {
            datas.outsideWorkingHours[tag] = 0;
          }
          datas.outsideWorkingHours[tag] += dataPoint.value;
        }
      });
    });

    return datas;
  }, [data, timeRange]);

  return (
    <Box
      sx={{
        display: screenwidth > 768 ? 'flex' : 'block',
        height: '100%',
        marginTop: '20px',
      }}
    >
      <DonutChart
        theme={nightTheme}
        donutData={Object.keys(chartData.outsideWorkingHours).map((k) => {
          return {
            name: k,
            y: chartData.outsideWorkingHours[k],
          };
        })}
        isLoading={isLoading}
      />
      <DonutChart
        theme={totalTheme}
        donutData={Object.keys(chartData).map((item) => {
          const key = item as keyof ChartData;
          const tags = Object.keys(chartData[key]);

          return {
            name: item === 'insideWorkingHours' ? 'Inside Working Hours' : 'Outside Working Hours',
            y: tags.reduce((acc, t) => {
              return chartData[key][t] + acc;
            }, 0),
          };
        })}
        isLoading={isLoading}
      />
      <DonutChart
        theme={dayTheme}
        donutData={Object.keys(chartData.insideWorkingHours).map((k) => {
          return {
            name: k,
            y: chartData.insideWorkingHours[k],
          };
        })}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default TotalOverview;
